import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

import { cn } from "../utils";
import { Button } from "./button";
import { Input, type InputProps } from "./input";

export type SecretInputProps = Omit<InputProps, "type"> & {
  /**
   * Render the reveal toggle. Turn it off for fields where seeing the value
   * has no diagnostic worth and shoulder-surfing is the bigger risk — a
   * confirm-password box, say, where the point is retyping from memory.
   */
  revealable?: boolean;
  /**
   * Noun for the toggle's accessible name: "Show {revealLabel}" / "Hide
   * {revealLabel}". Name the field, not the widget — "client secret", "API
   * key" — so a screen reader announces which secret the toggle belongs to.
   */
  revealLabel?: string;
  /** Controlled reveal state. Omit to let the component own it. */
  revealed?: boolean;
  onRevealedChange?: (revealed: boolean) => void;
  /** Class for the positioning wrapper, not the `<input>` (that's `className`). */
  containerClassName?: string;
};

/**
 * A masked field with an optional show/hide toggle.
 *
 * Use this for every secret the user types or pastes — client secrets, API
 * keys, tokens, passwords. The reveal exists because the common failure with a
 * pasted credential is a bad paste (a truncated value, or the secret's ID
 * copied instead of its value) and you cannot spot either through dots.
 *
 * Reveal state is component-local and deliberately transient: it resets on
 * unmount, and callers must not persist it to storage or a URL. `autoComplete`
 * defaults to "off" so browsers don't offer to save one-shot credentials;
 * password-manager-backed fields (a real login) should pass their own.
 */
function SecretInput({
  revealable = true,
  revealLabel = "value",
  revealed,
  onRevealedChange,
  className,
  containerClassName,
  ...props
}: SecretInputProps) {
  const [uncontrolledRevealed, setUncontrolledRevealed] = React.useState(false);
  const isRevealed = revealed ?? uncontrolledRevealed;

  const toggle = () => {
    const next = !isRevealed;
    setUncontrolledRevealed(next);
    onRevealedChange?.(next);
  };

  const input = (
    <Input
      type={isRevealed ? "text" : "password"}
      autoComplete="off"
      spellCheck={false}
      className={cn(revealable && "pr-9", className)}
      {...props}
    />
  );

  if (!revealable) return input;

  return (
    <div className={cn("relative", containerClassName)}>
      {input}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={toggle}
        disabled={props.disabled}
        aria-label={isRevealed ? `Hide ${revealLabel}` : `Show ${revealLabel}`}
        aria-pressed={isRevealed}
        className="absolute inset-y-0 right-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
      >
        {isRevealed ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </Button>
    </div>
  );
}

export { SecretInput };
